import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import tempLogo from '../../static/images/temporary_logo.png';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { emptyFilters } from '../../redux/filtersSlice';
import { setScroll } from '../../redux/scrollSlice';
import useWindowWidth from '../../hooks/useWindowWidth.js';
import LanguageSelector from './LanguageSelector.jsx';
import { useTranslation } from 'react-i18next';

import facebookIcon from '../../static/images/social_icons/facebook.jpg';
import instagramIcon from '../../static/images/social_icons/instagram.jpg';
import tiktokIcon from '../../static/images/social_icons/tiktok.jpg';
import youtubeIcon from '../../static/images/social_icons/youtube.jpg';

// Voci del drawer. La voce "Shop" è stata rimossa: la route /shop è disattivata
// (commentata in App.js) finché il cliente non riattiva il negozio.
const MENU_ITEMS = [
    { link: '/', labelKey: 'NavBar.home', icon: 'fi fi-rr-home' },
    { link: '/all-activities', labelKey: 'NavBar.activities', icon: 'fi fi-rr-direction-signal' },
    { link: '/who-we-are', labelKey: 'NavBar.whoWeAre', icon: 'fi fi-rr-users-alt' },
    { link: '/partner', labelKey: 'NavBar.becomePartner', icon: 'fi fi-rr-handshake' },
    { link: '/contacts', labelKey: 'NavBar.contacts', icon: 'fi fi-rr-envelope' },
];

const SOCIAL_LINKS = [
    { url: 'https://www.facebook.com/profile.php?id=100093522121374', icon: facebookIcon, alt: 'Facebook' },
    { url: 'https://www.instagram.com/ticinowild/?igsh=NzJydm9nY3AycjFk#', icon: instagramIcon, alt: 'Instagram' },
    { url: 'https://www.tiktok.com/@ticinowild?is_from_webapp=1&sender_device=pc', icon: tiktokIcon, alt: 'TikTok' },
    { url: 'https://www.youtube.com/@TicinoWild', icon: youtubeIcon, alt: 'YouTube' },
];

const MobileBar = ({ isLogged, level }) => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();

    const handleTap = (link) => {
        setIsMenuOpen(false);
        link === "/" ? window.location.href = "/" : navigate(link);
    }

    // Gestisco il click sul logo
    const handleLogoClick = () => {
        dispatch(emptyFilters());
        navigate("/")
    }

    // Catturo il width per eliminare il logo (inline) su mobile
    const windowWidth = useWindowWidth();

    // Blocco lo scroll della pagina mentre il drawer è aperto
    useEffect(() => {
        dispatch(setScroll(!isMenuOpen));
        return () => { dispatch(setScroll(true)) };
    }, [isMenuOpen, dispatch]);

    // Chiudo il drawer a ogni cambio di rotta (es. back di sistema su Android)
    useEffect(() => {
        setIsMenuOpen(false);
    }, [location.pathname]);

    const drawer = (
        <AnimatePresence>
            {
                isMenuOpen &&
                <>
                    {/* Backdrop */}
                    <motion.div
                        // z-[48]: sopra la BaseBar (z-45), che qui va coperta — il drawer ha una
                        // chiusura propria — ma sotto il pannello del drawer (z-50).
                        className="fixed inset-0 bg-black/40 z-[48]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => setIsMenuOpen(false)}
                    />

                    {/* Pannello laterale */}
                    <motion.div
                        className="fixed top-0 left-0 bottom-0 w-[85%] max-w-[340px] bg-white z-50 flex flex-col shadow-xl rounded-r-2xl overflow-hidden"
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
                        style={{
                            // Stesso bordo di sicurezza del resto dell'app (`body` in index.css),
                            // così in app nativa il contenuto non finisce sotto la status bar.
                            paddingTop: 'calc(max(env(safe-area-inset-top), 20px) + 0.5rem)',
                            paddingBottom: 'calc(max(env(safe-area-inset-bottom), 20px) + 0.5rem)'
                        }}
                    >
                        {/* Header: logo + chiudi */}
                        <div className="flex items-center justify-between px-7 pb-5">
                            <img src={tempLogo} alt="TicinoWild Logo" className="w-[150px]" onClick={() => handleTap('/')} />
                            <button
                                type="button"
                                onClick={() => setIsMenuOpen(false)}
                                aria-label={t('NavBar.closeMenu')}
                                className="leading-none"
                            >
                                {/* X senza cerchio (fi-rr-cross), stesso verde e ingombro
                                    ottico della circle-xmark usata altrove */}
                                <i className="fi fi-rr-cross text-[#225424] text-xl leading-none"></i>
                            </button>
                        </div>

                        {/* Voci di menu */}
                        <nav className="flex-1 overflow-y-auto px-7">
                            {
                                MENU_ITEMS.map(({ link, labelKey, icon }) => (
                                    <div
                                        key={link}
                                        className="flex items-center gap-5 py-5 border-t border-neutral-200 cursor-pointer"
                                        onClick={() => handleTap(link)}
                                    >
                                        <i className={`${icon} text-2xl leading-none w-7 text-center`}></i>
                                        <div className="text-lg">{t(labelKey)}</div>
                                    </div>
                                ))
                            }
                            <div className="border-t border-neutral-200"></div>
                        </nav>

                        {/* Social */}
                        <div className="px-7 pt-5">
                            <div className="text-sm text-neutral-500 font-bold mb-3">{t('NavBar.followUs')}</div>
                            <div className="flex gap-3">
                                {
                                    SOCIAL_LINKS.map(({ url, icon, alt }) => (
                                        <Link key={alt} to={url} target="_blank" rel="noopener noreferrer">
                                            <img src={icon} alt={alt} className="w-9 rounded-full" />
                                        </Link>
                                    ))
                                }
                            </div>
                        </div>
                    </motion.div>
                </>
            }
        </AnimatePresence>
    );

    return (
        <div className='w-full flex'>

            {/* Left */}
            <div className='flex-1 flex items-center justify-start gap-2'>
                {/* Hamburger — visibile anche agli utenti non loggati: tutte le voci del drawer sono pagine pubbliche */}
                <button
                    type="button"
                    aria-label={t('NavBar.openMenu')}
                    className="flex flex-col justify-center items-center py-2 px-2 gap-[6px] cursor-pointer"
                    onClick={() => setIsMenuOpen(true)}>
                    <div className="w-[24px] h-[2px] bg-black"></div>
                    <div className="w-[24px] h-[2px] bg-black"></div>
                    <div className="w-[24px] h-[2px] bg-black"></div>
                </button>

                {/* Logo */}
                {
                    windowWidth > 320 &&
                    <img src={tempLogo} alt="TicinoWild Logo" className={`${windowWidth > 460 ? 'w-48' : (windowWidth > 400 ? 'w-[140px]' : 'w-[114px]')}`} onClick={handleLogoClick} />
                }
            </div>

            {/* Right */}

            {/* Accedi o registrati */}
            {
                !isLogged &&
                <div className="flex gap-2 items-center">
                    <Link to="/register-login?section=Login"><div className='bg-white py-2 px-3 rounded-xl'>{t('NavBar.signIn')}</div></Link>
                    <LanguageSelector />
                </div>

            }

            {/* Icona profilo */}
            {/* Add activity (admin) */}
            {
                isLogged &&
                <div className='flex gap-3 items-center'>
                    {
                        level === 2 &&
                        <div className='flex-1 flex items-center justify-end gap-3'>
                            <Link to="/handle-activities"><div className='flex gap-2 items-center border rounded-full py-2 px-2.5'>
                                <i className="fi fi-rr-list-timeline mb-[-5px]"></i>
                            </div></Link>
                            <Link to="/add-edit-activity"><div className='pt-1'><i className="fi fi-sr-square-plus text-4xl text-[#225424]"></i></div></Link>
                        </div>
                    }
                    <LanguageSelector />
                </div>
            }

            {/* Il drawer va in body via Portal: la Navbar è dentro contenitori con stacking context propri */}
            {typeof document !== 'undefined' && createPortal(drawer, document.body)}

        </div>
    )
}

export default MobileBar;
